import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Send, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { heroContent, programBenefits, wellnessDomains, wellnessImages, siteConfig } from '../data/mock';
import { toast } from 'sonner';
import * as LucideIcons from 'lucide-react';

const HomePage = () => {
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-8 h-8" /> : null;
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/capture-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Thank you! We\'ll be in touch soon.');
        setLeadData({ name: '', email: '', phone: '' });
        setLeadFormOpen(false);
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Content Left, Smaller Image Far Right */}
      <section className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        {/* Large Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[700px] h-[700px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Content - Left Side */}
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg border-2 border-[#1E3A8A]">
                <h1 className="text-[#1E3A8A] text-3xl md:text-4xl font-bold leading-tight mb-8">
                  Transform Your Life Through the{' '}
                  <span className="text-[#E31837]">7 Domains</span> of Wellness
                </h1>

                <p className="text-[#1E3A8A] text-xl md:text-2xl mb-10 leading-relaxed">
                  {heroContent.subheadline}
                </p>

                <div className="flex flex-col gap-4">
                  {/* First Row - Two buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a href={siteConfig.jotformIntakeLink} target="_blank" rel="noopener noreferrer">
                      <Button className="btn-primary w-full sm:w-auto text-lg px-8 py-4">
                        {heroContent.primaryCta}
                        <ArrowRight className="w-5 h-5" />
                      </Button>
                    </a>
                    
                    {/* Lead Capture Dialog */}
                    <Dialog open={leadFormOpen} onOpenChange={setLeadFormOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="lead-form-trigger-btn" className="btn-secondary w-full sm:w-auto text-lg px-8 py-4">
                          <Send className="w-5 h-5" />
                          Submit Your Information
                        </Button>
                      </DialogTrigger>
                    <DialogContent data-testid="lead-form-dialog" className="bg-white border-2 border-[#1E3A8A] max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-[#1E3A8A] text-2xl font-bold">
                          Submit your information to find out more
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleLeadSubmit} data-testid="lead-capture-form" className="space-y-6 mt-4">
                        <div>
                          <Label htmlFor="lead-name" className="text-[#1E3A8A] font-semibold">Name *</Label>
                          <Input
                            id="lead-name"
                            data-testid="lead-name-input"
                            value={leadData.name}
                            onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                            className="mt-2 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A]"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="lead-email" className="text-[#1E3A8A] font-semibold">Email *</Label>
                          <Input
                            id="lead-email"
                            data-testid="lead-email-input"
                            type="email"
                            value={leadData.email}
                            onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                            className="mt-2 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A]"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="lead-phone" className="text-[#1E3A8A] font-semibold">Phone</Label>
                          <Input
                            id="lead-phone"
                            data-testid="lead-phone-input"
                            type="tel"
                            value={leadData.phone}
                            onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                            className="mt-2 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A]"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          data-testid="lead-form-submit-btn"
                          className="btn-primary w-full text-lg"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                          <Send className="w-5 h-5" />
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                  
                  {/* Second Row - Visit IWMM Button centered */}
                  <div className="flex justify-center">
                    <a href="https://IWUMM.com" target="_blank" rel="noopener noreferrer">
                      <Button className="bg-[#E31837] text-white border-2 border-[#E31837] hover:bg-[#1E3A8A] hover:border-[#1E3A8A] text-lg px-8 py-4 font-semibold transition-all">
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Visit Integrated Wellness Medical
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Trust indicators - Centered */}
                <div className="flex flex-wrap items-center justify-center gap-6 mt-10 pt-8 border-t-2 border-[#1E3A8A]">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-[#E31837]" />
                    <span className="text-[#1E3A8A] font-semibold text-lg">Evidence-Based</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-[#E31837]" />
                    <span className="text-[#1E3A8A] font-semibold text-lg">Clinical Integration</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Image - Smaller, Far Right */}
            <div className="lg:col-span-5 animate-fade-in-up delay-200 flex justify-end">
              <div className="relative w-full max-w-md">
                <div className="border-4 border-[#1E3A8A] rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={heroContent.image}
                    alt="Life Coaching Session"
                    className="w-full h-[450px] object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Snapshot Section - With Silhouette Border */}
      <section className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        {/* Large Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1000px] h-[1000px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          {/* Silhouette Container */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
            <div className="text-center mb-16">
              <h2 className="text-[#1E3A8A] text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                The <span className="text-[#E31837]">7-Domain Wellness Wheel</span> Approach
              </h2>
              <p className="text-[#1E3A8A] text-xl md:text-2xl max-w-3xl mx-auto mb-8">
                A comprehensive framework addressing every aspect of your well-being
              </p>
              <a 
                href={siteConfig.explore7DomainsLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#1E3A8A] text-white font-semibold text-xl px-10 py-5 rounded-lg border-4 border-[#1E3A8A] hover:bg-[#E31837] hover:border-[#E31837] transition-all shadow-lg"
              >
                <ExternalLink className="w-6 h-6" />
                Explore the 7 Domains of Wellness
              </a>
            </div>

            {/* 7 Domains Grid - Centered */}
            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl">
                {/* First Row - 4 items */}
                {wellnessDomains.slice(0, 4).map((domain) => (
                  <Card
                    key={domain.id}
                    className="bg-white border-2 border-[#1E3A8A] hover:shadow-lg transition-all text-center"
                  >
                    <CardContent className="p-6">
                      <div
                        className="w-14 h-14 rounded-lg flex items-center justify-center mb-5 border-2 border-[#1E3A8A] mx-auto"
                        style={{ backgroundColor: '#F8F9FA' }}
                      >
                        <span className="text-[#1E3A8A]">
                          {getIcon(domain.icon)}
                        </span>
                      </div>
                      <h3 className="text-[#1E3A8A] font-bold text-xl md:text-2xl mb-3">{domain.name}</h3>
                      <p className="text-[#1E3A8A] text-base md:text-lg">{domain.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* Second Row - 3 items centered */}
            <div className="flex justify-center mt-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl">
                {wellnessDomains.slice(4, 7).map((domain) => (
                  <Card
                    key={domain.id}
                    className="bg-white border-2 border-[#1E3A8A] hover:shadow-lg transition-all text-center"
                  >
                    <CardContent className="p-6">
                      <div
                        className="w-14 h-14 rounded-lg flex items-center justify-center mb-5 border-2 border-[#1E3A8A] mx-auto"
                        style={{ backgroundColor: '#F8F9FA' }}
                      >
                        <span className="text-[#1E3A8A]">
                          {getIcon(domain.icon)}
                        </span>
                      </div>
                      <h3 className="text-[#1E3A8A] font-bold text-xl md:text-2xl mb-3">{domain.name}</h3>
                      <p className="text-[#1E3A8A] text-base md:text-lg">{domain.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section - With Silhouette Border & Larger Text */}
      <section className="section-padding bg-white relative overflow-hidden">
        {/* Large Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1100px] h-[1100px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content with Silhouette Border */}
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-2xl border-4 border-[#1E3A8A] shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
              <h2 className="text-[#1E3A8A] text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
                Why Choose Our <span className="text-[#E31837]">Coaching Program</span>?
              </h2>
              <p className="text-[#1E3A8A] text-xl md:text-2xl mb-10 leading-relaxed">
                Our program combines the best of human expertise with AI-powered support to deliver lasting transformation.
              </p>

              <div className="space-y-8">
                {programBenefits.map((benefit) => (
                  <div key={benefit.id} className="flex gap-5 group">
                    <div className="w-16 h-16 rounded-lg border-2 border-[#1E3A8A] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1E3A8A] group-hover:text-white transition-all bg-white">
                      <span className="text-[#1E3A8A] group-hover:text-white transition-all">{getIcon(benefit.icon)}</span>
                    </div>
                    <div>
                      <h4 className="text-[#1E3A8A] font-bold text-2xl md:text-3xl mb-2">{benefit.title}</h4>
                      <p className="text-[#1E3A8A] text-lg md:text-xl">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="border-4 border-[#1E3A8A] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={wellnessImages.meditation2}
                  alt="Wellness Meditation"
                  className="w-full h-[700px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - With Silhouette Border */}
      <section className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        {/* Large Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          {/* Silhouette Container */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)]">
            <div className="text-center mb-16">
              <h2 className="text-[#1E3A8A] text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Your Path to <span className="text-[#E31837]">Transformation</span>
              </h2>
              <p className="text-[#1E3A8A] text-xl md:text-2xl max-w-3xl mx-auto">
                A simple, structured approach to lasting change
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Complete Intake", desc: "10 minutes. Comprehensive health history and goals.", icon: "ClipboardList" },
                { step: "02", title: "AI Analysis", desc: "The Matrix Engine performs comprehensive analysis.", icon: "Cpu" },
                { step: "03", title: "Meet Your Coach", desc: "Review your personalized Wellness Blueprint.", icon: "Users" },
                { step: "04", title: "Transform", desc: "12 weeks of guided transformation and support.", icon: "Sparkles" },
              ].map((item, index) => (
                <div key={index} className="relative">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-14 left-1/2 w-full h-0.5 bg-[#1E3A8A]" />
                  )}
                  <div className="relative bg-white border-2 border-[#1E3A8A] rounded-xl p-8 text-center hover:shadow-lg transition-all">
                    <div className="w-16 h-16 rounded-full border-4 border-[#1E3A8A] flex items-center justify-center mx-auto mb-6 bg-white relative z-10">
                      <span className="text-[#E31837] text-2xl font-bold">{item.step}</span>
                    </div>
                    <h3 className="text-[#1E3A8A] font-bold text-xl md:text-2xl mb-3">{item.title}</h3>
                    <p className="text-[#1E3A8A] text-base md:text-lg">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        {/* Large Logo Background - 100% opaque */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Silhouette Container */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)] text-center">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-24 w-24 object-contain mx-auto mb-10"
              />
              <h2 className="text-[#1E3A8A] text-4xl md:text-5xl lg:text-6xl font-bold mb-8">
                Ready to Stop Guessing?
              </h2>
              <p className="text-[#1E3A8A] text-xl md:text-2xl mb-10">
                Your body has been trying to tell you something. Let's finally listen.
                Start your assessment today and unlock your personalized wellness roadmap.
              </p>
              <div className="flex flex-col sm:flex-row gap-5 justify-center">
                <a href={siteConfig.jotformIntakeLink} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#1E3A8A] text-white border-4 border-[#1E3A8A] hover:bg-[#E31837] hover:border-[#E31837] text-xl px-12 py-5 rounded font-semibold transition-all">
                    Start Your Assessment Now
                    <ArrowRight className="w-6 h-6 ml-2" />
                  </Button>
                </a>
              </div>
              <p className="text-[#1E3A8A] mt-8 text-xl">
                or call/text: <a href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`} className="text-[#E31837] hover:underline font-semibold">{siteConfig.phone}</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
